"use client";

import { Header } from "@/components/header";
import { HeroSection } from "@/components/hero-section";
import { TeamCategory, TeamMember } from "@/data/team-data";
import { useMemo, useState, useEffect } from "react";
import { useInView } from "@/hooks/useInView";
import { employeeApi, contentInfoApi } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

import dynamic from "next/dynamic";
import Image from "next/image";

// Lazy load Footer
const Footer = dynamic(() => import("@/components/footer").then(mod => ({ default: mod.Footer })), {
  loading: () => <div className="h-75 bg-gray-200 animate-pulse" />,
});

// Lazy load ChatWidget
const ChatWidget = dynamic(() => import("@/components/chat-widget"), {
  loading: () => null,
  ssr: false,
});

// Helper function to get initials from name
function getInitials(name: string): string {
  return name
    .split(" ")
    .map(part => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

// Helper function to generate a consistent color based on name
function getColorFromName(name: string): string {
  const colors = [
    "bg-blue-500",
    "bg-purple-500",
    "bg-green-500",
    "bg-orange-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-teal-500",
    "bg-red-500",
  ];
  const index = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[index % colors.length];
}

// Helper function to get priority for role sorting
function getRolePriority(role: string): number {
  const lowerRole = role.toLowerCase();
  if (lowerRole.includes("president") || lowerRole.includes("ceo")) return 1;
  if (lowerRole.includes("vice president") || lowerRole.includes("vp")) return 2;
  return 3;
}

// Helper function to sort team members
function sortTeamMembers(members: TeamMember[], isManagementCategory: boolean): TeamMember[] {
  if (isManagementCategory) {
    return [...members].sort((a, b) => {
      const priorityA = getRolePriority(a.role);
      const priorityB = getRolePriority(b.role);
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      return a.name.localeCompare(b.name);
    });
  } else {
    return [...members].sort((a, b) => a.name.localeCompare(b.name));
  }
}

// Helper function to sort categories
function sortCategories(categories: TeamCategory[]): TeamCategory[] {
  return [...categories].sort((a, b) => {
    const aIsManagement = a.name.toLowerCase().includes("management");
    const bIsManagement = b.name.toLowerCase().includes("management");

    if (aIsManagement && !bIsManagement) return -1;
    if (!aIsManagement && bIsManagement) return 1;
    return a.name.localeCompare(b.name);
  });
}

export default function TeamPage() {
  const { language } = useLanguage();
  const [categories, setCategories] = useState<TeamCategory[]>([]);
  const [activeCategoryId, setActiveCategoryId] = useState<string>("");
  const [heroContent, setHeroContent] = useState<{
    content_title: string;
    content_description?: string | null;
  } | null>(null);

  const activeCategory = useMemo(
    () => categories.find((c) => c.id === activeCategoryId) ?? categories[0],
    [categories, activeCategoryId]
  );

  const [contentRef, contentInView] = useInView<HTMLDivElement>({ threshold: 0.1 });
  const [cardsRef, cardsInView] = useInView<HTMLDivElement>({ threshold: 0.1 });

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await employeeApi.getAll({ limit: 1000, is_active: true, lang: language });
        const employees = response.data as Array<{
          id_employee: string;
          name: string;
          university?: string | null;
          picture?: string | null;
          email?: string | null;
          bio?: string | null;
          linkedin?: string | null;
          is_active?: boolean;
          display_order?: number;
          position?: string | null;
          category?: string | null;
          id_category?: string | null;
          position_ref?: { id_position: string; name_position: string } | null;
          category_ref?: { id_category: string; name_category: string } | null;
        }>;

        const activeEmployees = employees.filter((employee) => employee.is_active !== false);
        const grouped = new Map<string, TeamCategory>();

        activeEmployees.forEach((employee) => {
          const categoryId = employee.category_ref?.id_category ?? employee.id_category ?? "uncategorized";
          const categoryName = employee.category_ref?.name_category ?? employee.category ?? "Uncategorized";

          if (!grouped.has(categoryId)) {
            grouped.set(categoryId, {
              id: categoryId,
              name: categoryName,
              members: [],
            });
          }

          const member: TeamMember = {
            id: employee.id_employee,
            name: employee.name,
            role: employee.position_ref?.name_position ?? employee.position ?? "",
            institution: employee.university ?? "",
            image: employee.picture ?? "",
            email: employee.email ?? undefined,
            bio: employee.bio ?? undefined,
            linkedin: employee.linkedin ?? undefined,
          };

          grouped.get(categoryId)?.members.push(member);
        });

        // Sort categories and their members
        const categoryList = Array.from(grouped.values()).map((category) => ({
          ...category,
          members: sortTeamMembers(category.members, category.name.toLowerCase().includes("management")),
        }));

        const sortedCategories = sortCategories(categoryList);
        setCategories(sortedCategories);
        setActiveCategoryId((current) => current || sortedCategories[0]?.id || "");
      } catch (error) {
        console.log("Failed to load employees:", error);
      }
    };

    fetchEmployees();
  }, [language]);

  useEffect(() => {
    let isMounted = true;

    const fetchHeroContent = async () => {
      try {
        const response = await contentInfoApi.getAll({ limit: 1, type: "team", lang: language });
        const data = (response.data?.data || response.data) as Array<{
          content_title: string;
          content_description?: string | null;
        }>;

        if (!isMounted) return;
        setHeroContent(data?.[0] || null);
      } catch (err) {
        if (isMounted) setHeroContent(null);
        console.log("Error fetching team hero content:", err);
      }
    };

    fetchHeroContent();
    return () => {
      isMounted = false;
    };
  }, [language]);

  return (
    <div className="page-layout bg-white">
      <div className="page-content">
        <Header activePage="home" />

        <HeroSection
          title={heroContent?.content_title || "Our Team"}
          subtitle={heroContent?.content_description || "Meet the talented individuals behind GaneshaIT"}
        />

        <section className="py-12 px-8 lg:px-16 bg-white">
          <div className="max-w-7xl mx-auto">
            <div ref={contentRef} className={`grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10 reveal ${contentInView ? 'is-visible' : ''}`}>
              {/* Left menu — OLD boxed design */}
              <aside className="pt-4">
                <div className="space-y-2">
                  {categories.map((cat) => {
                    const isActive = cat.id === activeCategoryId;

                    return (
                      <button
                        key={cat.id}
                        onClick={() => setActiveCategoryId(cat.id)}
                        className={`
                        w-full text-left px-4 py-3 rounded-lg transition-all
                        flex items-center justify-between
                        ${isActive
                            ? "bg-[#0C1A2E] text-white font-semibold"
                            : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                          }
                    `}
                      >
                        <span>{cat.name}</span>

                        <svg
                          className={`w-5 h-5 ${isActive ? "text-white" : "text-gray-400"
                            }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    );
                  })}
                </div>
              </aside>


              {/* Cards grid (FIRST image style) */}
              <div ref={cardsRef} className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-10 reveal ${cardsInView ? 'is-visible reveal-delay-200' : ''}`}>
                {activeCategory?.members?.map((m) => (
                  <div
                    key={m.id}
                    className="relative pt-10 group"
                  >
                    {/* floating avatar */}
                    <div className="absolute left-1/2 -top-1 -translate-x-1/2">
                      <div className="w-45 h-30 rounded-full bg-white flex items-center justify-center">
                        <div className="w-35 h-35 rounded-full bg-white shadow-[0_10px_22px_rgba(0,0,0,0.18)] overflow-hidden">
                          {m.image ? (
                            <Image
                              src={m.image}
                              alt={m.name}
                              width={140}
                              height={140}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className={`w-full h-full flex items-center justify-center ${getColorFromName(m.name)} text-white text-4xl font-bold`}>
                              {getInitials(m.name)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* card */}
                    <div className="rounded-[14px] border border-gray/800 bg-white px-10 pb-8 pt-16 text-center h-55 flex flex-col justify-center">
                      <h3 className="text-[16px] font-semibold text-[#0C1A2E] mt-15">
                        {m.name}
                      </h3>

                      <p className="text-[13px] font-semibold text-[#285494] mt-3">
                        {m.role}
                      </p>

                      <p className="text-[13px] text-gray-400 mt-0">
                        {m.institution}
                      </p>

                      {/* Social Links */}
                      <div className="flex gap-4 justify-center mt-4">
                        {m.linkedin && (
                          <a
                            href={m.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#285494] hover:text-[#0C1A2E] transition-colors"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                            </svg>
                          </a>
                        )}
                        {m.email && (
                          <a
                            href={`https://mail.google.com/mail/?view=cm&fs=1&to=${m.email}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#285494] hover:text-[#0C1A2E] transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* end grid */}
            </div>
          </div>
        </section>
      </div>

      <Footer />
      {/* Floating Chat Widget */}
      <ChatWidget />
    </div>
  );
}
